import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { LanguageEnum } from './../../common/enum';
import {
  AppError,
  BadInputError,
  ConflictError,
  IneligibleError,
  NoDataError,
  NotFoundError,
  UnauthorizedError,
} from './../../common/errors';
import { LanguageService } from './../../helper';
import { ThemesService } from 'src/app/core/services/themes.service';
import { CssThemes } from 'src/app/shared/common';

@Injectable({
  providedIn: 'root',
})
export class BaseAPI {
  constructor(
    private http: HttpClient,
    private languageService: LanguageService,
    private themesService: ThemesService
  ) {}

  public get(url: string, options?: any): Observable<any> {
    if (options) {
      options['observe'] = 'response';
      options['headers'] = this.getHeaders()
    } else {
      options = {
        observe: 'response',
        headers: this.getHeaders(),
      };
    }
    return this.http.get(this.encodeURL(url), options).pipe(
      tap((res : any) => {
        this.checkStatusCode(res["body"]);
      }),
      map((response: any) => {
        if (response.status === 204) {
          throw new NoDataError();
        } else {
          return response.body;
        }
      }),
      catchError(this.handleError)
    );
  }

  public post(url: string, body: any, options?: any): Observable<any> {
    if (!options) {
      options = {
        headers: this.getHeaders(),
      };
    }
    return this.http
      .post(this.encodeURL(url), body, options)
      .pipe(tap(this.checkStatusCode),catchError(this.handleError));
  }

  public put(url: string, body: any, options?: any): Observable<any> {
    if (!options) {
      options = {
        headers: this.getHeaders(),
      };
    }
    return this.http.put(url, body, options).pipe(tap(this.checkStatusCode),catchError(this.handleError));
  }

  public delete(url: string, options?: any): Observable<any> {
    if (!options) {
      options = {
        headers: this.getHeaders(),
      };
    }
    return this.http
      .delete(this.encodeURL(url), options)
      .pipe(tap(this.checkStatusCode),catchError(this.handleError));
  }

  public patch(url: string, body: any, options?: any): Observable<any> {
    if (!options) {
      options = {
        headers: this.getHeaders(),
      };
    }
    return this.http
      .patch(this.encodeURL(url), body, options)
      .pipe(tap(this.checkStatusCode),catchError(this.handleError));
  }

  public getHeaders(acceptLanguage?: string): HttpHeaders {
    if (!acceptLanguage) {
      if (this.languageService) {
        acceptLanguage = this.languageService.currentLanguage$.value.isoCode;
      } else {
        acceptLanguage = LanguageEnum.Arabic;
      }
    }
    return new HttpHeaders({
      'Accept-Language': acceptLanguage,
      'App-Type': this.themesService.currentTheme === CssThemes.SUPER_ADMIN ? '' : this.themesService.currentTheme 
    });
  }

  public encodeURL(url: string): string {
    return url.replace(' ', '%20');
  }

  public setQueryParams(queries: object): HttpParams {
    let queryParams = new HttpParams();
    for (const query in queries) {
      if (queries.hasOwnProperty(query)) {
        if (Array.isArray(queries[query])) {
          queries[query].forEach((item, index) => {
            queryParams = queryParams.append(`${query}`, item);
          });
        } else {
          queryParams = queryParams.append(query, queries[query]);
        }
      }
    }
    return queryParams;
  }

  handleError(response: any) {
    if (response.status === 400) {
      return throwError(new BadInputError(response.error ? response.error : { message: response.message }));
    } else if (response.status === 404) {
      return throwError(new NotFoundError(response.error ? response.error : { message: response.message }));
    } else if (response.status === 401) {
      return throwError(new UnauthorizedError(response.error ? response.error : { message: response.message }));
    } else if (response.status === 403) {
      return throwError(new IneligibleError(response.error ? response.error : { message: response.message }));
    } else if (response.status === 409) {
      return throwError(new ConflictError(response.error ? response.error : { message: response.message }));
    } else if (response.status === 204) {
      return throwError(new NoDataError(response.error ? response.error : { message: response.message }));
    }
    return throwError(new AppError(response.error ? response.error : { message: response.message }));
  }

  private checkStatusCode(res: any) : void {
    if ((res && res['statusCode'] >= 200 && res['statusCode'] < 300) || !res || res && !res['statusCode']) {
      return res;
    } else {
      res['status'] = res['statusCode'];
      throw res;
    }
  }
}
